/**
 * @file teamService.ts
 * @brief Team management: KRA/KPI via backend API
 * @note Performance review methods return empty data - backend endpoints not yet implemented
 */

import apiClient, { unwrapResponse } from './apiClient';
import { API_ENDPOINTS } from './endpoints';
import type {
  KRA,
  KPI,
  TeamMemberPerformance,
  PerformanceReview,
  TeamMemberDetail,
  ApiResponse,
} from '@/types';

const wrapResponse = <T>(data: T): ApiResponse<T> => ({
  data,
  message: 'Success',
  success: true,
});

function mapRawToKRA(raw: Record<string, unknown>): KRA {
  return {
    id: String(raw.id ?? ''),
    title: String(raw.title ?? ''),
    description: String(raw.description ?? ''),
    ownerId: String(raw.ownerId ?? ''),
    teamId: raw.teamId != null ? String(raw.teamId) : undefined,
    weight: Number(raw.weight ?? 0),
    status: (raw.status as KRA['status']) ?? 'draft',
    progress: Number(raw.progress ?? 0),
    relatedProjectIds: Array.isArray(raw.relatedProjectIds) ? (raw.relatedProjectIds as string[]) : [],
    relatedTaskIds: Array.isArray(raw.relatedTaskIds) ? (raw.relatedTaskIds as string[]) : [],
    startDate: String(raw.startDate ?? ''),
    endDate: String(raw.endDate ?? ''),
    kpiIds: [],
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

function mapRawToKPI(raw: Record<string, unknown>): KPI {
  return {
    id: String(raw.id ?? ''),
    kraId: String(raw.kraId ?? ''),
    title: String(raw.title ?? ''),
    description: raw.description != null ? String(raw.description) : undefined,
    type: (raw.type as KPI['type']) ?? 'number',
    frequency: (raw.frequency as KPI['frequency']) ?? 'monthly',
    targetValue: Number(raw.targetValue ?? 0),
    actualValue: Number(raw.actualValue ?? 0),
    unit: raw.unit != null ? String(raw.unit) : undefined,
    measurementCriteria: String(raw.measurementCriteria ?? ''),
    lastUpdatedAt: String(raw.lastUpdatedAt ?? raw.updatedAt ?? ''),
    history: [],
    createdAt: String(raw.createdAt ?? ''),
    updatedAt: raw.updatedAt != null ? String(raw.updatedAt) : undefined,
  };
}

// ============================================================================
// TEAM MANAGEMENT SERVICE
// ============================================================================

export const teamManagementService = {
  async getTeamMembers(teamId?: string): Promise<ApiResponse<unknown[]>> {
    if (!teamId) return wrapResponse([]);
    try {
      const response = await apiClient.get(API_ENDPOINTS.TEAMS.MEMBERS(teamId));
      const data = unwrapResponse<unknown[]>(response);
      return wrapResponse(Array.isArray(data) ? data : []);
    } catch {
      return wrapResponse([]);
    }
  },

  async getTeamMemberDetail(userId: string): Promise<ApiResponse<TeamMemberDetail | null>> {
    return wrapResponse(null);
  },

  // --------------------------------------------------------------------------
  // KRA (backend API)
  // --------------------------------------------------------------------------

  async getAllKRAs(filters?: { ownerId?: string; teamId?: string; page?: number; pageSize?: number }): Promise<ApiResponse<KRA[]>> {
    const params = new URLSearchParams();
    if (filters?.ownerId) params.set('ownerId', filters.ownerId);
    if (filters?.teamId) params.set('teamId', filters.teamId);
    if (filters?.page != null) params.set('page', String(filters.page));
    if (filters?.pageSize != null) params.set('pageSize', String(filters.pageSize));
    const url = params.toString() ? `${API_ENDPOINTS.KRAS.LIST}?${params}` : API_ENDPOINTS.KRAS.LIST;
    const response = await apiClient.get(url);
    const result = unwrapResponse<{ items?: Record<string, unknown>[] }>(response);
    const items = Array.isArray(result?.items) ? result.items : [];
    return wrapResponse(items.map(mapRawToKRA));
  },

  async getTeamMemberKRAs(userId: string): Promise<ApiResponse<KRA[]>> {
    const response = await apiClient.get(`${API_ENDPOINTS.KRAS.LIST}?ownerId=${encodeURIComponent(userId)}`);
    const result = unwrapResponse<{ items?: Record<string, unknown>[] }>(response);
    const items = Array.isArray(result?.items) ? result.items : [];
    return wrapResponse(items.map(mapRawToKRA));
  },

  async getKRAById(kraId: string): Promise<ApiResponse<KRA | null>> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.KRAS.GET(kraId));
      const data = unwrapResponse<Record<string, unknown>>(response);
      return wrapResponse(data ? mapRawToKRA(data) : null);
    } catch {
      return wrapResponse(null);
    }
  },

  async createKRA(kraData: Partial<KRA>): Promise<ApiResponse<KRA>> {
    const response = await apiClient.post(API_ENDPOINTS.KRAS.CREATE, kraData);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return wrapResponse(mapRawToKRA(data));
  },

  async updateKRA(kraId: string, updates: Partial<KRA>): Promise<ApiResponse<KRA | null>> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.KRAS.UPDATE(kraId), updates);
      const data = unwrapResponse<Record<string, unknown>>(response);
      return wrapResponse(data ? mapRawToKRA(data) : null);
    } catch {
      return wrapResponse(null);
    }
  },

  async deleteKRA(kraId: string): Promise<ApiResponse<boolean>> {
    try {
      await apiClient.delete(API_ENDPOINTS.KRAS.DELETE(kraId));
      return wrapResponse(true);
    } catch {
      return wrapResponse(false);
    }
  },

  // --------------------------------------------------------------------------
  // KPI (backend API)
  // --------------------------------------------------------------------------

  async getAllKPIs(kraId?: string): Promise<ApiResponse<KPI[]>> {
    if (!kraId) {
      const krasRes = await this.getAllKRAs({ pageSize: 100 });
      const kras = krasRes.data ?? [];
      const allKpis: KPI[] = [];
      for (const kra of kras) {
        const res = await apiClient.get(API_ENDPOINTS.KRAS.KPIS(kra.id));
        const items = unwrapResponse<Record<string, unknown>[]>(res);
        allKpis.push(...(Array.isArray(items) ? items.map(mapRawToKPI) : []));
      }
      return wrapResponse(allKpis);
    }
    const response = await apiClient.get(API_ENDPOINTS.KRAS.KPIS(kraId));
    const items = unwrapResponse<Record<string, unknown>[]>(response);
    return wrapResponse(Array.isArray(items) ? items.map(mapRawToKPI) : []);
  },

  async getTeamMemberKPIs(userId: string): Promise<ApiResponse<KPI[]>> {
    const krasRes = await this.getTeamMemberKRAs(userId);
    const kras = krasRes.data ?? [];
    const allKpis: KPI[] = [];
    for (const kra of kras) {
      const res = await apiClient.get(API_ENDPOINTS.KRAS.KPIS(kra.id));
      const items = unwrapResponse<Record<string, unknown>[]>(res);
      allKpis.push(...(Array.isArray(items) ? items.map(mapRawToKPI) : []));
    }
    return wrapResponse(allKpis);
  },

  async getKPIById(kpiId: string): Promise<ApiResponse<KPI | null>> {
    try {
      const response = await apiClient.get(API_ENDPOINTS.KPIS.GET(kpiId));
      const data = unwrapResponse<Record<string, unknown>>(response);
      return wrapResponse(data ? mapRawToKPI(data) : null);
    } catch {
      return wrapResponse(null);
    }
  },

  async createKPI(kpiData: Partial<KPI>): Promise<ApiResponse<KPI>> {
    const kraId = kpiData.kraId;
    if (!kraId) throw new Error('kraId is required to create a KPI');
    const response = await apiClient.post(API_ENDPOINTS.KRAS.CREATE_KPI(kraId), kpiData);
    const data = unwrapResponse<Record<string, unknown>>(response);
    return wrapResponse(mapRawToKPI(data));
  },

  async updateKPI(kpiId: string, actualValue: number, notes?: string): Promise<ApiResponse<KPI | null>> {
    try {
      const response = await apiClient.put(API_ENDPOINTS.KPIS.UPDATE(kpiId), {
        actualValue,
        ...(notes != null && { notes }),
      });
      const data = unwrapResponse<Record<string, unknown>>(response);
      return wrapResponse(data ? mapRawToKPI(data) : null);
    } catch {
      return wrapResponse(null);
    }
  },

  async getKPIHistory(kpiId: string): Promise<ApiResponse<KPI['history']>> {
    return wrapResponse([]);
  },

  async deleteKPI(kpiId: string): Promise<ApiResponse<boolean>> {
    try {
      await apiClient.delete(API_ENDPOINTS.KPIS.DELETE(kpiId));
      return wrapResponse(true);
    } catch {
      return wrapResponse(false);
    }
  },

  // --------------------------------------------------------------------------
  // PERFORMANCE (TODO: Backend endpoints not yet implemented)
  // Performance reviews are different from task reviews and require separate backend implementation
  // --------------------------------------------------------------------------

  async getTeamPerformance(): Promise<ApiResponse<TeamMemberPerformance[]>> {
    // TODO: Implement backend endpoint GET /task_management/teams/:teamId/performance
    console.warn('getTeamPerformance: Backend endpoint not implemented yet');
    return wrapResponse([]);
  },

  async getUserPerformanceMetrics(
    userId: string,
    period?: { startDate: string; endDate: string }
  ): Promise<ApiResponse<TeamMemberPerformance | null>> {
    // TODO: Implement backend endpoint GET /task_management/performance/user/:userId/metrics
    console.warn('getUserPerformanceMetrics: Backend endpoint not implemented yet');
    return wrapResponse(null);
  },

  async getPerformanceReviews(userId?: string, status?: string): Promise<ApiResponse<PerformanceReview[]>> {
    // TODO: Implement backend endpoint GET /task_management/performance/reviews
    // Note: This is different from task reviews (code reviews)
    console.warn('getPerformanceReviews: Backend endpoint not implemented yet');
    return wrapResponse([]);
  },

  async createPerformanceReview(reviewData: Partial<PerformanceReview>): Promise<ApiResponse<PerformanceReview>> {
    // TODO: Implement backend endpoint POST /task_management/performance/reviews
    console.warn('createPerformanceReview: Backend endpoint not implemented yet');
    throw new Error('Performance reviews not yet implemented in backend');
  },

  async updatePerformanceReview(
    reviewId: string,
    updates: Partial<PerformanceReview>
  ): Promise<ApiResponse<PerformanceReview | null>> {
    // TODO: Implement backend endpoint PATCH /task_management/performance/reviews/:id
    console.warn('updatePerformanceReview: Backend endpoint not implemented yet');
    throw new Error('Performance reviews not yet implemented in backend');
  },

  async submitPerformanceReview(reviewId: string): Promise<ApiResponse<PerformanceReview | null>> {
    // TODO: Implement backend endpoint POST /task_management/performance/reviews/:id/submit
    console.warn('submitPerformanceReview: Backend endpoint not implemented yet');
    throw new Error('Performance reviews not yet implemented in backend');
  },
};

export default teamManagementService;
